import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';

import { GRADE_LABEL_KEY } from '../metricGrades';

import type { MetricGrade, MetricPresentation } from '../metricGrades';

/**
 * MetricCard — 지표 하나를 카드로. 레이더를 대신한다(티켓 02).
 *
 * 레이더를 버린 이유: 축마다 근거의 세기가 다른데 폴리곤은 전부 같은 무게로 보이게 만든다.
 * 카드는 **값 옆에 등급을 붙일 자리**가 있고, 축을 나중에 늘려도 과거 기록과의 비교가
 * 덜 깨진다.
 */

const GRADE_STYLE: Record<MetricGrade, string> = {
  solid: 'bg-primary/15 text-primary',
  proxy: 'bg-muted text-muted-foreground',
  weak: 'border border-dashed border-border text-muted-foreground',
};

interface MetricCardProps {
  card: MetricPresentation;
  /** 0~100 점수. */
  val: number;
  /** 점수 아래 한 줄(예: 원값 `12.4초`, 참고 구간). */
  detail?: string | null;
}

export const MetricCard = ({ card, val, detail }: MetricCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{t(card.labelKey)}</span>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            GRADE_STYLE[card.grade],
          )}
        >
          {t(GRADE_LABEL_KEY[card.grade])}
        </span>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold tabular-nums">{val}</span>
        <span className="text-sm text-muted-foreground">/ 100</span>
        {detail && (
          <span className="ml-auto text-sm text-muted-foreground">
            {detail}
          </span>
        )}
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.min(100, Math.max(0, val))}%` }}
        />
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        {t(card.noteKey)}
      </p>
    </div>
  );
};
